import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, Wallet, Send, Landmark, CheckCircle2, AlertTriangle, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MoneyPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/me/money`, auth())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load your wallet'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (!email) return toast.error('Enter the recipient email');
    if (!cents || cents < 100) return toast.error('Minimum transfer is $1.00');
    setBusy(true);
    try {
      await axios.post(`${API}/me/money/send`, { email, amount_cents: cents, note }, auth());
      toast.success(`${usd(cents)} sent to ${email}`);
      setEmail(''); setAmount(''); setNote('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setBusy(false); }
  };

  const connectBank = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/me/money/stripe/onboard`, {}, auth());
      window.location.href = r.data.onboarding_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start bank setup'); setBusy(false); }
  };

  const withdraw = async () => {
    const bal = data.balance_cents || 0;
    if (bal < 100) return toast.error('Nothing to withdraw yet');
    if (!window.confirm(`Withdraw ${usd(bal)} to your bank? It arrives in 1-2 business days, no fees.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/me/money/withdraw`, { amount_cents: bal }, auth());
      toast.success(`${usd(r.data.amount_cents)} on its way to your bank`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Withdrawal failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const kindLabel = { transfer_in: 'Received', transfer_out: 'Sent', withdrawal: 'Withdrawal', ticket_purchase: 'Ticket purchase', ticket_refund: 'Credit returned' };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 space-y-5" data-testid="money-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Wallet className="h-6 w-6 text-emerald-400" /> My Wallet</h1>
          <p className="text-sm text-muted-foreground">Send money to anyone on Eventism, or withdraw to your bank.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Card><CardContent className="py-4 space-y-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Balance</p>
            <p className="text-3xl font-extrabold" data-testid="money-balance">{usd(data.balance_cents)}</p>
            <p className="text-[11px] text-muted-foreground">Lifetime received: {usd(data.lifetime_received_cents)}</p>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={busy || !data.stripe.payouts_enabled || (data.balance_cents || 0) < 100}
              onClick={withdraw} data-testid="money-withdraw-btn">Withdraw to bank</Button>
            {!data.stripe.payouts_enabled && <p className="text-[10px] text-amber-400">Connect your bank to enable withdrawals.</p>}
          </CardContent></Card>

          <Card><CardContent className="py-4 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Landmark className="h-4 w-4 text-indigo-400" /> Bank connection</p>
            {data.stripe.payouts_enabled ? (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" data-testid="money-stripe-status"><CheckCircle2 className="h-3 w-3 mr-1" /> Ready — payouts enabled</Badge>
            ) : data.stripe.connected ? (
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="money-stripe-status"><AlertTriangle className="h-3 w-3 mr-1" /> Started — finish setup</Badge>
            ) : (
              <Badge variant="outline" data-testid="money-stripe-status">Not connected</Badge>
            )}
            <p className="text-[11px] text-muted-foreground">Free standard payouts, 1–2 business days. Secured by Stripe.</p>
            <Button size="sm" variant="outline" disabled={busy} onClick={connectBank} data-testid="money-connect-bank">
              {data.stripe.connected ? 'Manage / finish setup' : 'Connect bank account'}
            </Button>
          </CardContent></Card>
        </div>

        <Card className="border-indigo-500/30"><CardContent className="py-4 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Send className="h-4 w-4 text-indigo-400" /> Send money</p>
          <div className="flex flex-wrap gap-2">
            <Input className="h-9 text-sm w-56" placeholder="Their email" value={email} onChange={e => setEmail(e.target.value)} data-testid="money-send-email" />
            <Input className="h-9 text-sm w-28" type="number" min="1" step="0.01" placeholder="Amount $" value={amount} onChange={e => setAmount(e.target.value)} data-testid="money-send-amount" />
            <Input className="h-9 text-sm flex-1 min-w-[140px]" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} data-testid="money-send-note" />
            <Button className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={send} data-testid="money-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">They don't need an account yet — we'll email them and the money waits in their wallet.</p>
        </CardContent></Card>

        <div>
          <p className="text-sm font-semibold mb-2">Activity</p>
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="money-txn-table">
              <thead><tr className="bg-muted/40">
                <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">With</th><th className="text-right px-3 py-2">Amount</th>
              </tr></thead>
              <tbody>
                {data.transactions.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground text-xs">No activity yet.</td></tr>
                ) : data.transactions.map(t => (
                  <tr key={t.txn_id} className="border-t border-border/60">
                    <td className="px-3 py-1.5 whitespace-nowrap">{String(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center gap-1">
                        {t.amount_cents >= 0 ? <ArrowDownLeft className="h-3 w-3 text-emerald-400" /> : <ArrowUpRight className="h-3 w-3 text-rose-400" />}
                        {kindLabel[t.kind] || t.kind}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{t.counterparty}{t.note ? ` — ${t.note}` : ''}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${t.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{t.amount_cents < 0 ? '-' : '+'}{usd(Math.abs(t.amount_cents))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
